import { Localization } from "@viamap/viamap2-common";
import { useEffect, useState } from "react";
import { ADModalBody, AdvancedDragModal } from "src/componentsUtils/AdvancedDragModal";
import { DynamicDataDrivenLayer } from "src/managers/DynamicDataDrivenLayer";
import { VectorLayer } from "src/managers/VectorLayerFunc";
import { GeojsonWFS } from "src/managers/WFSGeojsonLayerFunc";
import { FeatureLayer } from "src/managers/WmsLayerFunc";
import { useWindowContext } from "src/WindowManager/useWindowContext";
import { closeWindow, ManagedWindow } from "src/WindowManager/WindowState";



export function LayerServiceInfo({ managedWindow, layer }: { managedWindow: ManagedWindow, layer?: unknown }) {
   const { dispatch: windowDispatch } = useWindowContext();

   const [loading, setLoading] = useState<boolean>(true);
   const [capabilities, setCapabilities] = useState<ServiceInfo | null>(null);

   useEffect(() => {
      const signal = new AbortController();
      console.log("Fetching capabilities for layerId:", layer);
      if (layer) {
         (async () => {
            const data = await getServiceInfo(layer);
            if (signal.signal.aborted) return;
            setCapabilities(data);
            setLoading(false);
         })();
      }
      return () => {
         signal.abort();
         setLoading(true);
      }
   }, [layer]);

   return (
      <AdvancedDragModal
         title="Info"
         window={managedWindow}
         onClose={() => {
            windowDispatch(closeWindow(managedWindow.id))
         }}
         PosDefault={{ width: "440px" }}
      >
         <ADModalBody>
            {loading ? (
               <p>Loading capabilities...</p>
            ) : capabilities ? (
               <table className="layer-service-info">
                  <tbody>
                     <tr><td><strong>{Localization.getText("Data source")}:</strong></td><td>{capabilities.source}</td></tr>
                     <tr><td><strong>{Localization.getText("Layer Name")}:</strong></td><td>{capabilities.layername}</td></tr>
                     <tr><td><strong>{Localization.getText("Technical Name")}:</strong></td><td>{capabilities.technicalName}</td></tr>
                     {capabilities.description && <tr><td><strong>{Localization.getText("Description")}:</strong></td><td>{capabilities.description}</td></tr>}
                     {capabilities.attribution && <tr><td><strong>{Localization.getText("Attribution")}:</strong></td><td>{capabilities.attribution}</td></tr>}
                     {capabilities.link && <tr><td><strong>{Localization.getText("Link")}:</strong></td><td><a href={capabilities.link} target="_blank" rel="noopener noreferrer">{capabilities.link}</a></td></tr>}
                     {capabilities.contact && <tr><td><strong>{Localization.getText("Contact")}:</strong></td><td>{capabilities.contact}</td></tr>}
                  </tbody>
               </table>
            ) : (
               <p>No capabilities available.</p>
            )}
         </ADModalBody>
      </AdvancedDragModal>
   )

}


async function getServiceInfo(layer: FeatureLayer | VectorLayer | GeojsonWFS | DynamicDataDrivenLayer | unknown, language: string = "da"): Promise<ServiceInfo | null> {
   console.log("Getting capabilities for layer:", layer);
   if (layer instanceof DynamicDataDrivenLayer) {
      return getVectorSourceInfo(layer, language);
   } else if (layer instanceof VectorLayer) {
      return getVectorSourceInfo(layer, language);
   } else if (layer instanceof GeojsonWFS) {
      return getGeojsonWFSInfo(layer, language);
   } else if (layer instanceof FeatureLayer) {
      return getFeatureLayerInfo(layer);
   }
   return null;
}

function getFeatureLayerInfo(layer: FeatureLayer): ServiceInfo {
   return {
      source: layer.layer.host.split(".").splice(1).join("."),
      layername: layer.layer.label,
      description: layer.layer["description"],
      technicalName: layer.layer.layers,
      attribution: layer.layer["attrib"],
   };
}

function translatedLabel(layer: FeatureLayer, language: string): string {
   if (layer.layer.translationTable) {
      return layer.layer.translationTable[language]?.[layer.layer.label] || layer.layer.label;
   }
   return layer.layer.label;
}

function getVectorSourceInfo(layer: VectorLayer | DynamicDataDrivenLayer, language: string): ServiceInfo {
   return {
      source: "Viamap Databehandling",
      layername: translatedLabel(layer, language),
      technicalName: layer.layer["sourceID"] ?? "",
      description: "Dette lag indeholder data fra en eller flere offentlige kilder, bemærk data er optimeret til visning i Mapit. Kontakt support for mere infomation.",
      attribution: layer.layer["attrib"],
      contact: "support@viamap.com"
   }
};

function getGeojsonWFSInfo(layer: GeojsonWFS, language: string): ServiceInfo {
   let lowerUrl = new URL((layer.layer["src"] as string).toLowerCase());
   return {
      source: lowerUrl.host.split(".").splice(1).join(".").split("/")[0],
      layername: translatedLabel(layer, language),
      technicalName: lowerUrl.searchParams?.get?.("typename") ?? lowerUrl.searchParams?.get?.("typenames") ?? "Ukendt",
      attribution: layer.layer["attrib"]
   }
}

type ServiceInfo = {
   source: string;
   layername: string;
   technicalName: string;
   description?: string;
   link?: string;
   contact?: string;
   attribution?: string;
}